import { AfterViewInit, Directive, ElementRef, HostListener, Input, OnDestroy, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

@Directive({
  selector: '[appImgMagnifier]',
})
/* inspired by https://www.w3schools.com/howto/tryit.asp?filename=tryhow_js_image_magnifier_glass
   & https://github.com/alecrios/image-loupe-js/blob/master/src/image-loupe.js */
export class ImgMagnifierDirective implements AfterViewInit, OnDestroy {
  @Input() public zoom: number;

  private originalImage: HTMLImageElement;
  private glass: HTMLDivElement;
  private width: number;
  private height: number;
  private borderWidth: number = 3;
  private offsetVal: number = -100;

  constructor(private elementRef: ElementRef, public sanitizer: DomSanitizer) {}

  @HostListener('load')
  public onLoad(): void {
    setTimeout(() => this.magnify(), 500);
  }

  public ngAfterViewInit(): void {
    this.originalImage = this.elementRef.nativeElement;
  }

  public ngOnDestroy(): void {
    this.removeEventListeners();
  }

  private addEventListeners(): void {
    this.originalImage.addEventListener('mousemove', this.moveMagnifier.bind(this));
    this.originalImage.addEventListener('touchmove', this.moveMagnifier.bind(this));
    this.glass.addEventListener('mousemove', this.moveMagnifier.bind(this));
    this.glass.addEventListener('touchmove', this.moveMagnifier.bind(this));
  }

  private removeEventListeners(): void {
    this.originalImage.removeEventListener('mousemove', this.moveMagnifier.bind(this));
    this.originalImage.removeEventListener('touchmove', this.moveMagnifier.bind(this));
    this.glass.removeEventListener('mousemove', this.moveMagnifier.bind(this));
    this.glass.removeEventListener('touchmove', this.moveMagnifier.bind(this));
  }

  private magnify(): void {
    if (!this.originalImage) {
      return;
    }

    this.glass = document.createElement('div');
    this.glass.setAttribute('class', 'img-magnifier-glass');
    this.originalImage.parentElement.insertBefore(this.glass, this.originalImage);
    this.glass.style.width = '120px';
    this.glass.style.height = '120px';
    this.glass.style.backgroundImage = this.sanitizer.sanitize(SecurityContext.STYLE, `url("${this.originalImage.src}")`);
    this.glass.style.backgroundRepeat = 'no-repeat';
    this.glass.style.backgroundSize = `${this.originalImage.width * this.zoom}px ${this.originalImage.height * this.zoom}`;
    this.glass.style.backgroundColor = 'white';
    this.glass.style.position = 'absolute';
    this.glass.style.border = '3px solid #0cc';
    this.glass.style.borderRadius = '50%';
    this.glass.style.cursor = 'none';

    this.width = this.glass.offsetWidth / 2;
    this.height = this.glass.offsetHeight / 2;

    this.addEventListeners();
  }

  private moveMagnifier(e: MouseEvent): void {
    let pos: { x: number; y: number }, x: number, y: number;
    e.preventDefault();

    pos = this.getCursorPos(e);
    x = pos.x;
    y = pos.y;

    if (x > this.originalImage.width - this.width / this.zoom) {
      x = this.originalImage.width - this.width / this.zoom;
    }
    if (x < this.width / this.zoom) {
      x = this.width / this.zoom;
    }
    if (y > this.originalImage.height - this.height / this.zoom) {
      y = this.originalImage.height - this.height / this.zoom;
    }
    if (y < this.height / this.zoom) {
      y = this.height / this.zoom;
    }

    this.glass.style.left = x - this.width + 'px';
    this.glass.style.top = y - this.height + 'px';
    this.glass.style.backgroundPosition = `-${x * this.zoom - this.width + this.borderWidth - this.offsetVal}px -${
      y * this.zoom - this.height + this.borderWidth - this.offsetVal
    }px`;
  }

  private getCursorPos(e: MouseEvent): { x: number; y: number } {
    let x: number = 0,
      y: number = 0;
    const rect: DOMRect = this.originalImage.getBoundingClientRect();
    e = (e || window.event) as MouseEvent;
    x = e.pageX - rect.left;
    y = e.pageY - rect.top;
    x = x - window.pageXOffset;
    y = y - window.pageYOffset;
    return { x: x, y: y };
  }
}
